import * as React from 'react'
import { Script, graphql, useStaticQuery } from 'gatsby'
import StructureData from '@components/head/StructureData/StructureData'
import { Breadcrumbs, FilePath } from '@type/component-data-types'
import convertToISOLikeFormat from '@assets/utility/convertToISOLikeFormat'
import { FAQItem } from '@type/component-data-types'

type Props = {
  pageTitle?: string
  description?: string
  isTop?: boolean
  breadcrumbs?: Breadcrumbs
  structureDataOption?: React.ReactNode
  children?: React.ReactNode
  ogpImagePath?: FilePath
  publishDate?: string
  modifiedDate?: string
  faq?: FAQItem[]
  mainEntityOfPage?: string // 記事の正規URL
  about?: string // 記事の主要トピック
  keywords?: string[] // 記事に関連するキーワード配列
}

const MetaElements = (props: Props): React.JSX.Element => {
  // gqlでgatsby-config.tsからサイトで共通の値を取得する
  const data = useStaticQuery(graphql`
    query getSiteMetaData {
      site {
        siteMetadata {
          title
          description
          faviconPath
          ogpImagePath
          siteUrl
        }
      }
    }
  `)

  const isTop = props.isTop ?? false

  let pageTitle = ''
  if (isTop) {
    pageTitle = props.pageTitle || data.site.siteMetadata.title
  } else {
    pageTitle =
      `${props.pageTitle} | ${data.site.siteMetadata.title} ` ||
      data.site.siteMetadata.title
  }

  const description = props.description || data.site.siteMetadata.description,
    faviconPath = data.site.siteMetadata.faviconPath,
    siteUrl = `${data.site.siteMetadata.siteUrl}`,
    ogpImagePath = props.ogpImagePath
      ? `${siteUrl}/ogp/${props.ogpImagePath}`
      : data.site.siteMetadata.ogpImagePath

  const publishDate = props.publishDate
    ? convertToISOLikeFormat(props.publishDate)
    : undefined

  const modifiedDate = props.modifiedDate
    ? convertToISOLikeFormat(props.modifiedDate)
    : undefined

  const mainEntityOfPage = props.mainEntityOfPage
    ? `${data.site.siteMetadata.siteUrl}${props.mainEntityOfPage}`
    : undefined

  return (
    <>
      {/* title-metaデータ */}
      <meta charSet="utf-8" />
      <title>{pageTitle}</title>
      <link rel="shortcut icon" type="image/x-icon" href={faviconPath} />
      <meta httpEquiv="Cache-Control" content="no-cache" />
      <meta name="description" content={description} />
      {/* ogpデータ */}
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={siteUrl} />
      <meta property="og:image" content={ogpImagePath} />
      {/* appleやsafari用のmeta情報 */}
      <meta name="apple-mobile-web-app-title" content={pageTitle} />
      {/* memo: 連携ASP_impactのサイト証明タグ */}
      {/* memo: valueは本来のmeta要素では利用せず、linterのエラーとなるため、ts-ignoreで回避しています */}
      {/* @ts-ignore */}
      <meta name="impact-site-verification" value="911825391" />
      {/* 構造化データ */}
      <StructureData
        breadcrumb={props.breadcrumbs}
        pageTitle={props.pageTitle}
        publishDate={publishDate}
        modifiedDate={modifiedDate}
        faq={props.faq}
        mainEntityOfPage={mainEntityOfPage}
        about={props.about}
        keywords={props.keywords}
        description={description}
        articleImagePath={ogpImagePath}
      >
        {props.structureDataOption}
      </StructureData>
      {/* google tag manager  */}
      <noscript>{`<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NSF744X" height="0" width="0" style="display:none;visibility:hidden"></iframe>`}</noscript>
      <Script>{`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-NSF744X');`}</Script>
      {props.children}
    </>
  )
}

export default MetaElements
